import * as React from 'react'
import { useSnapshot } from 'valtio'
import { dayHistoryState } from '../../../../store/dayHistoryState.ts'
import { historyState } from '../../../../store/historyState.ts'
import { DayHistoryPolyline } from './DayHistoryPolyline.tsx'
import { DayHistoryMarker } from './DayHistoryMarker.tsx'
import { DayHistoryPlayback } from './DayHistoryPlayback.tsx'

/**
 * Container for all Day History map elements.
 * Only renders when day history mode is active and data is loaded.
 * This component is rendered inside the history map view.
 */
export const DayHistoryMap: React.FC = () => {
    const active = (useSnapshot(dayHistoryState) as typeof dayHistoryState).active
    const pathData = (useSnapshot(dayHistoryState) as typeof dayHistoryState).pathData
    const loading = (useSnapshot(dayHistoryState) as typeof dayHistoryState).loading
    const map = (useSnapshot(historyState) as typeof historyState).map

    // Fit the map to show the entire day's path when data loads
    React.useEffect(() => {
        if (!map || !pathData || pathData.path.length === 0) return

        const bounds = new google.maps.LatLngBounds()
        pathData.path.forEach(point => {
            bounds.extend({ lat: point.lat, lng: point.lng })
        })

        // Trigger resize so Google Maps recalculates container size,
        // then wait for idle before fitting bounds
        google.maps.event.trigger(map, 'resize')
        const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
            map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 })
        })

        return () => {
            google.maps.event.removeListener(listener)
        }
    }, [map, pathData])

    if (!active || !pathData || loading) return null

    // Empty path — the controls overlay handles the "no activity" message
    if (pathData.path.length === 0) return null

    return (
        <>
            <DayHistoryPlayback />
            <DayHistoryPolyline />
            <DayHistoryMarker />
        </>
    )
}
